"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { createContext, useContext } from "react";

import {
  ensureRecentTapTracking,
  wasRecentTouchOrPenPress,
} from "../lib/recent-tap";
import { cn } from "../lib/utils";

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider delay={delay} {...props} />;
}

/**
 * Whether the surrounding `Tooltip` is in informational mode. Base UI
 * tooltips open on hover/focus only, so on touch devices an informational
 * trigger — one whose sole job is showing this bubble — would be
 * unreachable. Informational mode renders Base UI's Popover instead,
 * styled as a tooltip: `openOnHover` keeps the desktop hover behavior,
 * and touch gets the popover's native tap-to-open, tap-again/outside
 * dismissal, and Escape for free. Tooltips on action controls must NOT
 * opt in — there, tap means the action.
 */
const InformationalContext = createContext(false);

const TOOLTIP_POPUP_CLASSES =
  "bg-surface-hover text-foreground z-50 w-fit origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs text-balance drop-shadow-sm transition-[opacity,scale] duration-150 data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0";

const TOOLTIP_ARROW_CLASSES =
  "z-50 flex data-[side=top]:-bottom-[3px] data-[side=bottom]:-top-[3px] data-[side=left]:-right-[3px] data-[side=right]:-left-[3px]";

function Tooltip({
  onOpenChange,
  informational = false,
  disabled,
  ...props
}: TooltipPrimitive.Root.Props & {
  /**
   * Mark the trigger as informational-only: its sole job is showing this
   * tooltip. Renders as a tooltip-styled popover so touch devices can tap
   * it open (see InformationalContext). Leave off for tooltips on action
   * controls — there, tap must stay the action.
   */
  informational?: boolean;
}) {
  ensureRecentTapTracking();
  if (informational) {
    const { children, open, defaultOpen } = props;
    return (
      <InformationalContext.Provider value={true}>
        <PopoverPrimitive.Root
          data-slot="tooltip"
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={
            onOpenChange as unknown as PopoverPrimitive.Root.Props["onOpenChange"]
          }
        >
          {children}
        </PopoverPrimitive.Root>
      </InformationalContext.Provider>
    );
  }
  return (
    <InformationalContext.Provider value={false}>
      <TooltipProvider>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          disabled={disabled}
          onOpenChange={(open, eventDetails) => {
            // Hover tooltips are hover-only BY CONTRACT (see `informational`
            // above: on action controls, tap means the action). Base UI's
            // hover path already ignores touch, but pencil presses count as
            // mouse-like and WebKit's tap-synthesized compatibility hover
            // events carry no pointer type — either opens a tooltip that no
            // hover-end will ever dismiss, and with one provider per Tooltip
            // stuck ones accumulate across triggers. A real hover never
            // starts with a touch/pen press, so veto exactly those opens.
            // Device-level gating would be wrong here: an iPad trackpad
            // hovers with a real mouse pointer, and keyboard focus opens
            // (reason "trigger-focus") must keep working everywhere.
            if (
              open &&
              eventDetails.reason === "trigger-hover" &&
              wasRecentTouchOrPenPress()
            ) {
              eventDetails.cancel();
              return;
            }
            // Radix parity: an open tooltip must not swallow Escape. Base UI
            // stops propagation after dismissing the tooltip, so a dialog
            // underneath needs a second press; letting the event propagate
            // dismisses both layers on one Esc (and un-sticks contentless
            // tooltips, which are open-but-invisible and otherwise eat a
            // keypress with no feedback).
            if (!open && eventDetails.reason === "escape-key") {
              eventDetails.allowPropagation();
            }
            onOpenChange?.(open, eventDetails);
          }}
          {...props}
        />
      </TooltipProvider>
    </InformationalContext.Provider>
  );
}

function TooltipTrigger({
  onClick,
  // Tooltip-flavored handle/payload types don't fit the popover trigger and
  // have no meaning in informational mode — dropped there.
  handle,
  payload,
  nativeButton = false,
  ...props
}: TooltipPrimitive.Trigger.Props & {
  /**
   * Informational mode only: whether the element supplied via `render` is
   * a native `<button>`. Defaults to false because informational triggers
   * are usually info icons and meters; a trigger that IS a native button
   * (a `Button` explaining why it's disabled) says so, and Base UI then
   * leaves the browser's own Enter/Space-to-click alone instead of stamping
   * `role="button"` and its non-native key handling over it.
   */
  nativeButton?: boolean;
}) {
  const informational = useContext(InformationalContext);
  if (informational) {
    return (
      <PopoverPrimitive.Trigger
        data-slot="tooltip-trigger"
        // Hover still opens on hover-capable devices (tooltip parity, same
        // zero delay as TooltipProvider's); on touch there is no hover and
        // the popover's own click-to-open takes over.
        openOnHover
        delay={0}
        nativeButton={nativeButton}
        {...props}
        onClick={(event) => {
          onClick?.(event);
          // Modified and non-primary clicks keep the browser's native
          // affordances (open-in-new-tab when the trigger sits inside a
          // link) — same guard as HoverCard's trigger.
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }
          // An informational trigger's activation must not also activate
          // whatever it sits on. stopPropagation silences upstream
          // listeners; preventDefault cancels an ancestor link's NATIVE
          // navigation (which stopPropagation alone leaves running — and
          // suppressing the row's React handler suppresses the very
          // preventDefault that normally keeps it client-side). Base UI's
          // own open/close toggle is unaffected: it is only cancelled via
          // its preventBaseUIHandler mechanism, not defaultPrevented.
          event.preventDefault();
          event.stopPropagation();
        }}
      />
    );
  }
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onClick={onClick}
      handle={handle}
      payload={payload}
      {...props}
    />
  );
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 0,
  align = "center",
  alignOffset = 0,
  anchor,
  arrowless,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "side" | "sideOffset" | "align" | "alignOffset" | "anchor"
  > & {
    arrowless?: boolean;
  }) {
  const informational = useContext(InformationalContext);
  const popupClassName = cn(
    TOOLTIP_POPUP_CLASSES,
    arrowless &&
      "data-[side=bottom]:mt-2.5 data-[side=left]:mr-2.5 data-[side=right]:ml-2.5 data-[side=top]:mb-2.5",
    className,
  );
  if (informational) {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          anchor={anchor}
          className="isolate z-50"
        >
          <PopoverPrimitive.Popup
            data-slot="tooltip-content"
            // It reads as a tooltip, so it must behave like one for focus:
            // never pull focus in on open (hover-opens especially) nor yank
            // it back on close.
            initialFocus={false}
            finalFocus={false}
            className={cn(popupClassName, "outline-none")}
            {...(props as PopoverPrimitive.Popup.Props)}
          >
            {!arrowless && (
              <PopoverPrimitive.Arrow className={TOOLTIP_ARROW_CLASSES}>
                <div className="bg-surface-hover size-2.5 rotate-45 rounded-[2px]" />
              </PopoverPrimitive.Arrow>
            )}
            {children}
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    );
  }
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={popupClassName}
          {...props}
        >
          {!arrowless && (
            <TooltipPrimitive.Arrow className={TOOLTIP_ARROW_CLASSES}>
              <div className="bg-surface-hover size-2.5 rotate-45 rounded-[2px]" />
            </TooltipPrimitive.Arrow>
          )}
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
